import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { ForensicsFormDialog } from '../components/ForensicsFormDialog'
import { ForensicsDeleteDialog } from '../components/ForensicsDeleteDialog'
import { ForensicsViewDialog } from '../components/ForensicsViewDialog'
import { useForensicsEnvironments, useDeleteForensicsEnvironment } from '../hooks'
import { createForensicsColumns } from '../columns'
import type { ForensicsEnvironment } from '@/types/entities'

export function ForensicsListPage() {
    const { t } = useTranslation()
    const [selectedEnv, setSelectedEnv] = useState<ForensicsEnvironment | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)

    const { data: environments, isLoading } = useForensicsEnvironments()
    const deleteEnv = useDeleteForensicsEnvironment()

    const envList: ForensicsEnvironment[] = Array.isArray(environments) ? environments : []

    const handleEdit = (env: ForensicsEnvironment) => {
        setSelectedEnv(env)
        setShowEditDialog(true)
    }

    const handleDelete = (env: ForensicsEnvironment) => {
        setSelectedEnv(env)
        setShowDeleteDialog(true)
    }

    const handleView = (env: ForensicsEnvironment) => {
        setSelectedEnv(env)
        setShowViewDialog(true)
    }

    const handleDeleteConfirm = () => {
        if (selectedEnv) {
            deleteEnv.mutate(selectedEnv.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedEnv(null)
                },
            })
        }
    }

    const columns = createForensicsColumns(handleEdit, handleDelete, handleView, t)

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('forensics.loading')}</div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">{t('forensics.title')}</h1>
                    <p className="text-muted-foreground">{t('forensics.description')}</p>
                </div>
                <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('forensics.createEnvironment')}
                </Button>
            </div>

            <DataTable
                columns={columns}
                data={envList}
                searchKey="name"
                searchPlaceholder={t('forensics.searchPlaceholder')}
            />

            <ForensicsFormDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                environment={null}
            />

            <ForensicsFormDialog
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
                environment={selectedEnv}
            />

            <ForensicsDeleteDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                environment={selectedEnv}
                onConfirm={handleDeleteConfirm}
                isLoading={deleteEnv.isPending}
            />

            <ForensicsViewDialog
                open={showViewDialog}
                onOpenChange={setShowViewDialog}
                environment={selectedEnv}
            />
        </div>
    )
}
